/**
 * screens/hooks/useQueueConfig.ts
 * Live config state for the Operator (Tier-2) and Config (Tier-1) screens.
 * Loads the queue, exposes cfg2/cfg1, and PATCHes a single changed field with
 * an optimistic local update + refetch (the catalog/crm write pattern). Pass
 * null to disable (memory mode / no active queue) — then it holds nothing.
 */

import { useEffect, useState, useCallback } from 'react';
import { getQueue, patchOperator, patchConfig } from '../../services/queue/queueService';
import { dtoToCfg2, dtoToCfg1, cfg2ToPatch, cfg1ToPatch } from '../../services/queue/queueMappers';
import { handleApiError } from '../../utils/errorHandler';
import type { Cfg1, Cfg2 } from '../../queue/types';

interface UseQueueConfigResult {
  cfg2: Cfg2 | null;
  cfg1: Cfg1 | null;
  loading: boolean;
  saving: boolean;
  /** Set a Tier-2 field (optimistic), PATCH, then refetch to confirm. */
  setTier2: <K extends keyof Cfg2>(key: K, val: Cfg2[K]) => void;
  setTier1: <K extends keyof Cfg1>(key: K, val: Cfg1[K]) => void;
}

export function useQueueConfig(queueId: string | null): UseQueueConfigResult {
  const [cfg2, setCfg2] = useState<Cfg2 | null>(null);
  const [cfg1, setCfg1] = useState<Cfg1 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { queue } = await getQueue(id);
      setCfg2(dtoToCfg2(queue));
      setCfg1(dtoToCfg1(queue));
    } catch (err) {
      handleApiError(err, 'Could not load queue config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!queueId) { setCfg2(null); setCfg1(null); return; }
    load(queueId);
  }, [queueId, load]);

  const setTier2 = useCallback(<K extends keyof Cfg2>(key: K, val: Cfg2[K]) => {
    if (!queueId || !cfg2) return;
    const next = { ...cfg2, [key]: val };
    setCfg2(next); // optimistic
    setSaving(true);
    patchOperator(queueId, cfg2ToPatch(next))
      .then(() => load(queueId)) // refetch to confirm the server's view
      .catch((err) => { handleApiError(err, 'Could not save'); load(queueId); })
      .finally(() => setSaving(false));
  }, [queueId, cfg2, load]);

  const setTier1 = useCallback(<K extends keyof Cfg1>(key: K, val: Cfg1[K]) => {
    if (!queueId || !cfg1) return;
    const next = { ...cfg1, [key]: val };
    setCfg1(next);
    setSaving(true);
    patchConfig(queueId, cfg1ToPatch(next))
      .then(() => load(queueId))
      .catch((err) => { handleApiError(err, 'Could not save'); load(queueId); })
      .finally(() => setSaving(false));
  }, [queueId, cfg1, load]);

  return { cfg2, cfg1, loading, saving, setTier2, setTier1 };
}
